// ════════════════════════════════════════════════════════════════════════════
//  ManDana — approving a screen that cannot scan
//  ---------------------------------------------------------------------------
//  A desktop has no camera, so it cannot photograph the code Settings shows.
//  The flow turns around instead: the desktop shows a code and waits, somebody
//  points THIS phone's ordinary camera at it, the camera app sees a link, and
//  the app opens here holding `?grant=CODE`.
//
//  THE PRESS IS THE ONLY GATE, and everything on this surface follows from that.
//  Minting a pairing code needs no account — it cannot, the screen asking for one
//  has no session yet — so anybody can put a code in front of anybody and ask
//  them to approve it. No check on the server can tell that apart from the real
//  thing: both are a signed-in person approving a code they were shown. What
//  stands between the two is a person reading one sentence, so the sentence says
//  plainly what approving does, and the code is repeated here to be held against
//  the screen it is supposed to be on.
//
//  SO NOTHING HAPPENS ON ARRIVAL. Opening the link draws this card and stops.
//  A surface that granted on mount would turn a scanned QR into a silent
//  takeover, and the scan is the easy half of this flow for an attacker.
//
//  AND THE CODE LEAVES THE ADDRESS AS IT IS READ (app/md-qr.ts): once, at the
//  first render that asks for it, memoised here so a re-render, a StrictMode
//  double-invoke or a second mount cannot lose it. Reload after that and there
//  is nothing to approve — which is correct: the code was already delivered.
//
//  Exposes <MdPairApproval> and mdPairArrival.
// ════════════════════════════════════════════════════════════════════════════
const { useState: useStP } = React;
const { Glass: GlassP, ink: inkP, surf: surfP, mdRgba: rgbaP, mdIsLight: isLightP } = window;

/* The code this page was opened with, asked for exactly once.
   `undefined` means nobody has asked yet; null means there was nothing to take.
   The memo matters because taking is destructive — the second call to
   mdTakeGrantParam answers null by design, and without this the first re-render
   would blank the card the person is reading. */
let mdPairTaken;
function mdPairArrival() {
  if (mdPairTaken === undefined) {
    mdPairTaken = (typeof window.mdTakeGrantParam === 'function'
      ? window.mdTakeGrantParam() : null) || null;
  }
  return mdPairTaken;
}

/* What each refusal means to the person holding the phone. `no_offer` is the
   one that carries the weight: three different server states — never existed,
   expired, already granted — arrive as one answer on purpose (a distinguishing
   reply would tell a stranger which codes are live), so the words have to cover
   all three without guessing between them.

   SHARED WITH SETTINGS, which approves the same code by typing rather than by
   camera. Two spellings of "that code is not recognised" would be two things to
   keep true, and the second one always drifts. */
const MD_PAIR_REFUSAL = {
  no_offer: 'That code is not recognised. It may have expired, or already been used — ask the other screen to show a new one.',
  missing_code: 'No code was given, so nothing was approved.',
  not_signed_in: 'Your session has ended. Sign in again, then approve from the other screen.',
  unreachable: 'No answer from the server. Nothing was approved.',
  malformed: 'The server answered something this app could not read. Nothing was approved.',
  failed: 'That did not go through, and nothing was approved.',
};
function mdPairRefusalWords(error) {
  return MD_PAIR_REFUSAL[error] || MD_PAIR_REFUSAL.failed;
}

/* The sentence the whole flow rests on, in one place because it appears in two:
   the surface a scanned code opens, and the Settings row a typed one goes into.
   It names who the other screen becomes rather than describing a mechanism,
   because that is the part a person can actually check. */
const MD_PAIR_WARNING = 'Approve only if this code is on a screen in front of you — '
  + 'approving signs that screen in as you, with everything in your account.';

/**
 * The one question a scanned pairing code asks.
 *
 * Takes no props: the code comes from the address, and everything else is this
 * component's own. Mounted unconditionally by the app — with no code in the
 * address it draws nothing at all, which is every boot but this one.
 */
function MdPairApproval({ accent = '#7C68E6' }) {
  const light = isLightP();
  const [code, setCode] = useStP(mdPairArrival);
  const [stage, setStage] = useStP('ask');
  const [busy, setBusy] = useStP(false);
  const [refused, setRefused] = useStP(null);

  /* Escape and browser Back put it away, through the one stack the whole app
     shares. Dismissing grants nothing, so this is the safe direction — and it
     is only armed while there is something standing. */
  window.useMdDismissLayer(() => { if (!busy) setCode(null); }, !!code, null, 'confirm');

  if (!code) return null;

  const approve = async () => {
    if (busy) return;
    setRefused(null);
    setBusy(true);
    const res = await window.mdPairGrant(code);
    setBusy(false);
    if (!res || !res.ok) { setRefused((res && res.error) || 'failed'); return; }
    setStage('done');
  };

  const plane = window.mdSceneRegistry?.().confirm?.plane ?? window.MD_SHEET_ASK_Z ?? 200;
  const title = { color: inkP(0.95), fontSize: 18, fontWeight: 700, marginBottom: 8 };
  const prose = { margin: '0 0 16px', color: inkP(0.58), fontSize: 13.5, lineHeight: 1.55 };
  const primary = { width: '100%', marginTop: 18, padding: '12px 14px', borderRadius: 12,
    cursor: busy ? 'default' : 'pointer', border: 0,
    background: busy ? inkP(0.1) : rgbaP(accent, 1), color: busy ? inkP(0.32) : '#fff',
    fontSize: 14.5, fontWeight: 700, letterSpacing: '.01em' };
  const quiet = { width: '100%', marginTop: 9, padding: '10px', borderRadius: 11,
    cursor: 'pointer', background: 'transparent', border: `1px solid ${inkP(0.14)}`,
    color: inkP(0.66), fontSize: 13.5, fontWeight: 600 };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: plane, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      background: light ? 'rgba(239,241,244,0.9)' : 'rgba(4,6,12,0.84)',
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      animation: 'mdConfIn .2s ease' }}>
      <GlassP radius={22} alpha={0.12} style={{ padding: 22, width: 'min(420px, 100%)',
        maxHeight: '100%', overflow: 'auto',
        animation: 'mdConfPop .26s cubic-bezier(.22,1,.36,1)' }}>
        <div data-md-pair={stage} role="dialog" aria-modal="true"
          aria-label={stage === 'done' ? 'The other screen is signing in' : 'Sign in another screen'}>

          {stage === 'ask' && <React.Fragment>
            <div style={title}>Sign in another screen?</div>
            <p style={prose}>
              A screen showing this code has asked to be signed in.
            </p>

            <div data-md-pair-code style={{ padding: '15px 12px', borderRadius: 12,
              textAlign: 'center', background: surfP(0.14),
              border: `1px solid ${rgbaP(accent, 0.4)}`, color: inkP(0.95),
              fontSize: 22, fontWeight: 680, letterSpacing: '.16em', wordBreak: 'break-all',
              userSelect: 'text', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {code}
            </div>

            {/* The whole security of the flow, in the words a person can act on.
                Not softened, and not phrased as a policy: it names who the other
                screen becomes, and the one thing to check before pressing. */}
            <p style={{ margin: '14px 0 0', color: inkP(0.72), fontSize: 13, lineHeight: 1.6 }}>
              {MD_PAIR_WARNING}
            </p>

            {refused && <p data-md-pair-refusal role="status" style={{ margin: '14px 0 0',
              color: inkP(0.72), fontSize: 12.5, lineHeight: 1.55 }}>
              {mdPairRefusalWords(refused)}
            </p>}

            {/* Nothing here reaches for focus on mount, unlike every other panel
                in this app. There is no field to fill, and putting the caret on
                Approve would leave the dangerous half of a security question
                under whatever the person's next keypress happened to be. */}
            <button type="button" style={primary} disabled={busy} onClick={approve}>
              {busy ? 'Approving…' : 'Approve'}
            </button>
            <button type="button" style={quiet} disabled={busy}
              onClick={() => setCode(null)}>Cancel</button>
          </React.Fragment>}

          {stage === 'done' && <React.Fragment>
            <div style={title}>The other screen is signing in</div>
            <p style={{ ...prose, marginBottom: 0 }}>
              It may take a few seconds to notice. Nothing else is needed here — and this
              code cannot be used again.
            </p>
            <button type="button" style={{ ...primary, marginTop: 20 }}
              onClick={() => setCode(null)}>Done</button>
          </React.Fragment>}
        </div>
      </GlassP>
    </div>,
    document.body,
  );
}

Object.assign(window, {
  MdPairApproval, mdPairArrival, mdPairRefusalWords, MD_PAIR_WARNING,
});
